import { motion } from 'framer-motion';

const INFORMAL_NOTE = [
  "I honestly did not want to make this boring legal stuff, but since this website uses Discord login, stores internal company records, and sends some activity logs to GTY management channels, it is better to be clear about it.",
  "This is not here to scare anyone. It is here so that if somebody ever decides to question how the site works, what data is used, or why actions are logged, GTY has a clear answer instead of a messy problem.",
  "A template was used for the structure of this document, but the GTY-specific data, systems, and details were added manually.",
  "By using this website, you understand that this is an internal GTY system and that certain actions may be recorded for management, security, and operational purposes.",
];

const PRIVACY_SECTIONS = [
  {
    title: '1. Information We Collect',
    body: [
      'When you use the GTY website, we may collect information such as your Discord ID, Discord username, display name, avatar, server roles, website login/session information, truck usage actions, payment proof uploads, LOA requests, CDL scheduling information, internal employee file information, uploaded images or documents, timestamps, and management notes related to GTY operations.',
      'We only collect information that is needed to operate GTY systems, manage members, verify activity, handle payments, maintain records, and protect the website from misuse.',
    ],
  },
  {
    title: '2. How We Use Information',
    body: [
      'GTY may use collected information to allow Discord login, verify membership and Discord roles, manage truck usage, log truck checkouts and returns, review payment proof, manage LOA requests, manage CDL scheduling, maintain internal employee records, send operational logs to GTY Discord management channels, resolve disputes, and protect GTY systems from abuse.',
    ],
  },
  {
    title: '3. Discord Logs',
    body: [
      'Certain actions on the GTY website may be sent to private GTY Discord channels for management review. Examples include using a truck, returning a truck, submitting payment proof, submitting LOA requests, management actions, and other important operational events.',
      'These logs are used for internal GTY recordkeeping, moderation, security, and management purposes.',
    ],
  },
  {
    title: '4. Cookies and Login Sessions',
    body: [
      'The GTY website may use necessary cookies, tokens, or local storage to keep users logged in and to make the website function properly.',
      'GTY does not use cookies for advertising or selling user data. If third-party analytics or tracking tools are added in the future, this Privacy Policy may be updated.',
    ],
  },
  {
    title: '5. Who Can See Your Information',
    body: [
      'Your information may be visible to GTY management, website administrators, authorized staff members, and Discord channels used for private GTY management logs.',
      'Your information is not sold.',
    ],
  },
  {
    title: '6. Third-Party Services',
    body: [
      'GTY may use third-party services to operate the website, including Discord for login, identity, roles, and log channels, as well as database, storage, and hosting providers.',
      'These services may process information as needed to provide their services.',
    ],
  },
  {
    title: '7. Data Storage and Retention',
    body: [
      'GTY may keep records for as long as they are needed for company operations, payment verification, employee records, moderation, dispute resolution, or security.',
      'Some records may be deleted when they are no longer needed, when a user leaves GTY, or when management decides they are no longer relevant.',
    ],
  },
  {
    title: '8. Data Deletion Requests',
    body: [
      'You may request that GTY delete or correct information related to you.',
      'Some records may need to be kept if they are required for management, safety, dispute resolution, payment verification, or abuse prevention. To request deletion or correction, contact GTY management.',
    ],
  },
  {
    title: '9. Changes to This Policy',
    body: [
      'GTY may update this Privacy Policy at any time. If important changes are made, GTY may notify users through the website, Discord, or another reasonable method.',
    ],
  },
  {
    title: '10. Contact',
    body: ['For questions about this Privacy Policy, contact GTY management.'],
  },
];

const TERMS_SECTIONS = [
  {
    title: '1. Internal GTY System',
    body: [
      'The GTY website is an internal system for GTY members, staff, and management. It may be used for truck usage, payments, LOA requests, CDL scheduling, employee files, management records, and Discord-related GTY operations.',
    ],
  },
  {
    title: '2. Discord Login',
    body: [
      'You may be required to log in with Discord to access certain features. By logging in, you allow the website to use your Discord account information, such as your Discord ID, username, avatar, and server roles, for GTY-related purposes.',
    ],
  },
  {
    title: '3. Activity Logging',
    body: [
      'Actions taken on the website may be logged. This includes actions such as using a truck, returning a truck, uploading payment proof, submitting forms, sending requests, and management updates.',
      'Some logs may be sent to GTY Discord management channels. These logs are used for operational records, moderation, security, and dispute resolution.',
    ],
  },
  {
    title: '4. User Responsibilities',
    body: [
      'You agree not to submit false information, upload fake payment proof, abuse website features, attempt to access restricted areas, interfere with website systems, misuse Discord login or another user account, delete or manipulate records without permission, or use the website in a way that harms GTY or its members.',
    ],
  },
  {
    title: '5. Uploads',
    body: [
      'If you upload images, screenshots, documents, or other files, you are responsible for making sure the content is accurate and appropriate.',
      'GTY may remove uploaded content if it is false, inappropriate, harmful, or no longer needed.',
    ],
  },
  {
    title: '6. Management Decisions',
    body: [
      'GTY management may use website records, Discord logs, payment records, LOA records, and other internal information when making company decisions. This may include decisions about truck access, payment status, employee records, LOA status, CDL scheduling, membership status, or disciplinary actions.',
    ],
  },
  {
    title: '7. No Guarantee of Access',
    body: [
      'Access to the GTY website may be changed, restricted, suspended, or removed at any time by GTY management. Access may depend on your Discord roles, membership status, or management approval.',
    ],
  },
  {
    title: '8. Website Availability',
    body: [
      'GTY will try to keep the website working, but the website may sometimes be unavailable due to maintenance, hosting issues, bugs, Discord issues, or other technical problems.',
      'GTY is not responsible for losses or issues caused by downtime or technical errors.',
    ],
  },
  {
    title: '9. Privacy',
    body: [
      'Your use of the website is also covered by the GTY Privacy Policy. By using the website, you understand that certain information and actions may be collected, stored, and logged for GTY purposes.',
    ],
  },
  {
    title: '10. Changes to These Terms',
    body: [
      'GTY may update these Terms at any time. If you continue using the website after changes are made, you accept the updated Terms.',
    ],
  },
  {
    title: '11. Contact',
    body: ['For questions about these Terms, contact GTY management.'],
  },
];

function LegalSection({ section }) {
  return (
    <section className="py-8 border-t-2 border-[#1c1c1f]">
      <h2 className="text-lg md:text-xl font-black uppercase tracking-tight text-white mb-4">
        {section.title}
      </h2>
      <div className="space-y-4">
        {section.body.map((paragraph) => (
          <p key={paragraph} className="text-sm md:text-base text-[#a1a1aa] leading-relaxed">
            {paragraph}
          </p>
        ))}
      </div>
    </section>
  );
}

export default function Legal({ type = 'privacy' }) {
  const isTerms = type === 'terms';
  const title = isTerms ? 'Terms of Service' : 'Privacy Policy';
  const subtitle = isTerms
    ? 'Rules for using the GTY website and related internal systems.'
    : 'How GTY collects, uses, stores, and shares information across the website.';
  const sections = isTerms ? TERMS_SECTIONS : PRIVACY_SECTIONS;

  return (
    <main className="min-h-screen pt-24 bg-[#0a0a0c]">
      <div className="max-w-5xl mx-auto px-6 md:px-12 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <span className="overline">GTY Internal Notice</span>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mt-4 mb-7 tracking-tighter uppercase leading-[0.92]">
            {title}
          </h1>
          <div className="grid md:grid-cols-[1fr_180px] gap-8 items-end border-b-4 border-[#7c3aed] pb-10">
            <p className="text-lg md:text-xl text-[#a1a1aa] leading-relaxed max-w-3xl">
              {subtitle}
            </p>
            <div className="md:text-right">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#52525b] font-black">
                Last Updated
              </div>
              <div className="mono-number text-white text-xl font-black mt-2">
                2026
              </div>
            </div>
          </div>
        </motion.div>

        <motion.aside
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.08 }}
          className="my-10 border-2 border-[#27272a] bg-[#101014] p-6 md:p-8"
        >
          <h2 className="text-sm font-black uppercase tracking-[0.25em] text-[#a855f7] mb-5">
            Informal Note
          </h2>
          <div className="space-y-4">
            {INFORMAL_NOTE.map((paragraph) => (
              <p key={paragraph} className="text-sm md:text-base text-[#d4d4d8] leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </motion.aside>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, delay: 0.14 }}
        >
          {sections.map((section) => (
            <LegalSection key={section.title} section={section} />
          ))}
        </motion.div>
      </div>
    </main>
  );
}
